import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { DataService } from '../core/data.service';
import { DaysOfWeek, EndCondition, FormMode, PatternType, Period, RecurrencyStatus, WeekOfMonth } from '../shared/enums';
import { TourTemplate } from '../shared/models/tour-template';
import { ModalService } from '../shared/modal/modal.service';
import { RecurrencyPattern } from '../shared/models/recurrency-pattern';
import { RecurrentOrderFormComponent } from './recurrent-order-form.component';
import { RecurrentLoadingFormComponent } from './recurrent-loading-form.component';
import { OrderTemplate } from '../shared/models/order-template';

const TIMEZONE_STORAGE_KEY: string = 'timeZone';

@Component({
  selector: 'app-recurrent-tour-form',
  templateUrl: 'recurrent-tour-form.component.html',
  styleUrls: ['recurrent-tour-form.component.css']
})
export class RecurrentTourFormComponent implements OnInit, OnChanges {
  @Input() tour;
  @Input() tourTemplate: TourTemplate;
  @Output() tourTemplateSaved = new EventEmitter();
  @ViewChild(NgForm) form: NgForm;
  enum = { FormMode, Period, PatternType, EndCondition };
  recurrencyStatus;
  periods;
  days;
  dayGroups;
  weeks;
  model;
  vehicles;
  timeZones;
  formMode: FormMode;
  tourTemplateModel: TourTemplate;
  recurrencyPatternModel: RecurrencyPattern;
  disableForm: boolean;
  orderLoadingList = [];
  newOrderIdKey = 0;
  newLoadingIdKey = 0;

  constructor(
    private dataService: DataService,
    private modalService: ModalService,
    private notificationsService: NotificationsService,
    private route: ActivatedRoute
  ) {
    let recurrencyStatus = Object.keys(RecurrencyStatus).map(s => ({ text: s, value: RecurrencyStatus[s] })).filter(d => typeof d.value === 'number');
    let days = Object.keys(DaysOfWeek).map(d => ({ text: d, value: DaysOfWeek[d] })).filter(d => typeof d.value === 'number');
    let weeks = Object.keys(WeekOfMonth).map(w => ({ text: w, value: WeekOfMonth[w] })).filter(w => typeof w.value === 'number');
    let periods = Object.keys(Period).map(p => ({ text: p, value: Period[p] })).filter(p => typeof p.value === 'number');

    this.recurrencyStatus = recurrencyStatus;
    this.days = days.filter(d => d.value && d.value.toString(2).match(/1/g).length === 1);
    this.dayGroups = days.filter(d => d.value && this.days.indexOf(d) === -1);
    this.weeks = weeks.filter(w => !/^None$/.test(w.text));
    this.periods = periods.filter(p => /^Week|Month$/.test(p.text)).map(p => ({ ...p, text: `${p.text}(s)` }));
  }

  get defaultTimeZone() {
    let stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    return this.timeZones && this.timeZones.map(t => t.id).filter(id => id === stored)[0] || 'UTC';
  }

  set defaultTimeZone(value) {
    if (value) {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(TIMEZONE_STORAGE_KEY);
    }
  }

  get IsReadOnly() {
    if (this.recurrencyPatternModel != null) {
      if (this.recurrencyPatternModel.status == RecurrencyStatus.Completed || this.recurrencyPatternModel.status == RecurrencyStatus.Cancelled)
        return true;
      else
        return false;
    } else {
      return false;
    }
  }

  get IsRecurrenceActive() {
    if (this.recurrencyPatternModel != null) {
      if (this.recurrencyPatternModel.status != RecurrencyStatus.Planned)
        return true;
      else
        return false;
    } else {
      return false;
    }
  }

  ngOnInit() {
    this.tour = this.tour || this.route.snapshot.data['tour'];
    this.formMode = this.tourTemplate ? FormMode.Edit : FormMode.Create;
    this.prepareModel();

    this.dataService.getVehicles().then(vehicles => {
      this.vehicles = vehicles;
    });

    this.dataService.getTimeZones().then(timeZones => {
      this.timeZones = timeZones;
      if (this.formMode === FormMode.Create) {
        this.recurrencyPatternModel.timeZoneName = this.defaultTimeZone;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tourTemplate && !changes.tourTemplate.firstChange) {
      let tourTemplate = changes.tourTemplate.currentValue;
      this.formMode = tourTemplate ? FormMode.Edit : FormMode.Create;
      this.prepareModel();
    }
  }

  onPeriodChange = (value) => {
    if (value === Period.Week) {
      this.recurrencyPatternModel = {
        ...this.recurrencyPatternModel,
        patternType: PatternType.DaysOfWeek,
        daysOfWeek: DaysOfWeek.None
      };
    } else if (value === Period.Month) {
      this.recurrencyPatternModel = {
        ...this.recurrencyPatternModel,
        patternType: PatternType.DayOfMonth,
        dayOfMonth: 1,
        weekOfMonth: WeekOfMonth.First,
        daysOfWeek: DaysOfWeek.Monday
       };
    }
  }

  prepareModel() {
    this.disableForm = false;
    if (this.formMode === FormMode.Create) {
      if (this.tour) {
        this.tourTemplate = TourTemplate.fromTour(this.tour);
        Object.assign(this.tourTemplate.recurrencyPattern, { timeZoneName: this.defaultTimeZone });
      } else {
        let recurrencyPattern = new RecurrencyPattern();
        this.tourTemplate = new TourTemplate();
        Object.assign(recurrencyPattern, { timeZoneName: this.defaultTimeZone });
        Object.assign(this.tourTemplate, { recurrencyPattern });
        this.disableForm = true;
      }
    }
    this.tourTemplateModel = { ...this.tourTemplate
                              , orderTemplates: JSON.parse(JSON.stringify(this.tourTemplate.orderTemplates))
                              , loadingTemplates: JSON.parse(JSON.stringify(this.tourTemplate.loadingTemplates))
                             };
    this.recurrencyPatternModel = { ...this.tourTemplate.recurrencyPattern };
    this.resetOrderLoadingList();
    console.log(this.orderLoadingList);
  }

  onSubmit(form) {
    if (form.invalid) {
      this.notificationsService.error('Unable to create template', 'There are missing or invalid values');
      return;
    }
    let template = { ...this.tourTemplateModel, recurrencyPattern: { ...this.recurrencyPatternModel } };

    if (this.formMode === FormMode.Edit) {
      this.dataService.updateTourTemplate(template).then(tourTemplate => {
        this.notificationsService.success('Template updated');
        this.tourTemplate = tourTemplate;
        this.formMode = FormMode.Edit;
        this.prepareModel();
        form.form.markAsPristine();
        this.tourTemplateSaved.emit(tourTemplate);
      }, err => {
        let msg = err.status === 500 ? 'An error occurred' : 'There are missing or invalid values';
        if (err.status === 400 && err.error != null && err.error.message === 'Validation error in RecurrencyPattern') {
          msg =  'Error occured.' ;
          if (err.error.errors != null) {
            for (let key in err.error.errors) {
              msg = msg + ', ' + err.error.errors[key];
            }
          }
        }
        this.notificationsService.error('Unable to update template', msg);
      });
    } else {
      this.dataService.createTourTemplate(template).then(tourTemplate => {
        this.notificationsService.success('Template created');
        this.tourTemplate = tourTemplate;
        this.formMode = FormMode.Edit;
        this.prepareModel();
        form.form.markAsPristine();
        this.tourTemplateSaved.emit(tourTemplate);
      }, err => {
        let msg = err.status === 500 ? 'An error occurred' : 'There are missing or invalid values.';
        if (err.status === 400 && err.error != null && err.error.message === 'Validation error in RecurrencyPattern') {
          msg =  'Error occured.' ;
          if (err.error.errors != null) {
            for (let key in err.error.errors) {
              msg = msg + ', ' + err.error.errors[key];
            }
          }
        }
        this.notificationsService.error('Unable to create template', msg);
      });
    }
  }

  revertChanges(form) {
    let clonedTemplate = JSON.parse(JSON.stringify(this.tourTemplate));
    this.tourTemplateModel = clonedTemplate;
    this.recurrencyPatternModel = clonedTemplate.recurrencyPattern;
    this.resetOrderLoadingList();
    form.form.markAsPristine();
  }

  addOrder() {
    let modal = this.modalService.open(RecurrentOrderFormComponent, { size: 'responsive' });
    let component = modal.componentInstance;
    component.companyId = this.tourTemplateModel.companyID;
    component.orderTemplateSaved.subscribe(orderTemplate => {
      console.log(orderTemplate);
      orderTemplate.tourTemplateId = this.tourTemplateModel.id;
      this.newOrderIdKey = this.newOrderIdKey - 1;
      orderTemplate.companyID = this.tourTemplateModel.companyID;
      this.tourTemplateModel.orderTemplates.push(orderTemplate);
      this.resetOrderLoadingList();
      this.form.form.markAsDirty();
    });
  }

  editOrder(orderTemplate) {
    let clonedOrderTemplate = JSON.parse(JSON.stringify(orderTemplate));
    let modal = this.modalService.open(RecurrentOrderFormComponent, { size: 'responsive' });
    let component = modal.componentInstance;
    if (clonedOrderTemplate.recurrencyPattern && clonedOrderTemplate.recurrencyPattern.startDate == '1753-01-01T00:00:00'){
      clonedOrderTemplate.recurrencyPattern.startDate = null;
    }
    component.orderTemplate = clonedOrderTemplate;
    component.companyId = this.tourTemplateModel.companyID;
    component.orderTemplateSaved.subscribe(orderTemplate => {
      console.log(orderTemplate);
      let itemIndex = this.tourTemplateModel.orderTemplates.findIndex(item  => item.id == orderTemplate.id);
      this.tourTemplateModel.orderTemplates[itemIndex] = orderTemplate;
      this.resetOrderLoadingList();
      this.form.form.markAsDirty();
    });
  }
  removeOrder(orderTemplate) {
    orderTemplate.deleted = true;
    if (orderTemplate.id < 1) {
      let itemIndex = this.tourTemplateModel.orderTemplates.indexOf(orderTemplate);
      this.tourTemplateModel.orderTemplates.splice(itemIndex , 1);
      this.resetOrderLoadingList();
    }
    this.form.form.markAsDirty();
  }
  addLoading() {
    let modal = this.modalService.open(RecurrentLoadingFormComponent, { size: 'responsive' });
    let component = modal.componentInstance;
    component.loadingTemplateSaved.subscribe(loadingTemplate => {
      console.log(loadingTemplate);
      loadingTemplate.tourTemplateId = this.tourTemplateModel.id;
      this.newLoadingIdKey = this.newLoadingIdKey - 1;
      loadingTemplate.companyID = this.tourTemplateModel.companyID;
      this.tourTemplateModel.loadingTemplates.push(loadingTemplate);
      this.resetOrderLoadingList();
      this.form.form.markAsDirty();
    });
  }

  editLoading(loadingTemplate) {
    let clonedLoadingTemplate = JSON.parse(JSON.stringify(loadingTemplate));
    if (clonedLoadingTemplate.recurrencyPattern && clonedLoadingTemplate.recurrencyPattern.startDate == '1753-01-01T00:00:00') {
      clonedLoadingTemplate.recurrencyPattern.startDate = null;
    }
    let modal = this.modalService.open(RecurrentLoadingFormComponent, { size: 'responsive' });
    let component = modal.componentInstance;
    component.loadingTemplate = clonedLoadingTemplate;
    component.loadingTemplateSaved.subscribe(loadingTemplate => {
      console.log(loadingTemplate);
      let itemIndex = this.tourTemplateModel.loadingTemplates.findIndex(item  => item.id == loadingTemplate.id);
      this.tourTemplateModel.loadingTemplates[itemIndex] = loadingTemplate;
      this.resetOrderLoadingList();
      this.form.form.markAsDirty();
    });
  }

  removeLoading(loadingTemplate) {
    loadingTemplate.deleted = true;
    if (loadingTemplate.id < 1) {
      let itemIndex = this.tourTemplateModel.loadingTemplates.indexOf(loadingTemplate);
      this.tourTemplateModel.loadingTemplates.splice(itemIndex , 1);
      this.resetOrderLoadingList();
    }
    this.form.form.markAsDirty();
  }
  moveItemDown(index) {
    let nextIndex = this.getNextActiveIndex(index);
   if (this.orderLoadingList[index] && nextIndex && this.orderLoadingList[nextIndex]) {
      if (this.orderLoadingList[index].isOrder) {
        if (this.orderLoadingList[nextIndex].isOrder) {
          if (this.orderLoadingList[index].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[index].orderTemplate.deliveryDateTo
            && this.orderLoadingList[nextIndex].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[nextIndex].orderTemplate.deliveryDateTo) {
          let tempDateFrom = this.orderLoadingList[nextIndex].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[nextIndex].orderTemplate.deliveryDateFrom = this.orderLoadingList[index].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[index].orderTemplate.deliveryDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[nextIndex].orderTemplate.deliveryDateTo;
          this.orderLoadingList[nextIndex].orderTemplate.deliveryDateTo = this.orderLoadingList[index].orderTemplate.deliveryDateTo;
          this.orderLoadingList[index].orderTemplate.deliveryDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/next item. Please set the valid timeframe to move.');
          }
        } else {
          if (this.orderLoadingList[index].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[index].orderTemplate.deliveryDateTo
            && this.orderLoadingList[nextIndex].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[nextIndex].loadingTemplate.loadingDateTo) {
          let tempDateFrom = this.orderLoadingList[nextIndex].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[nextIndex].loadingTemplate.loadingDateFrom = this.orderLoadingList[index].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[index].orderTemplate.deliveryDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[nextIndex].loadingTemplate.loadingDateTo;
          this.orderLoadingList[nextIndex].loadingTemplate.loadingDateTo = this.orderLoadingList[index].orderTemplate.deliveryDateTo;
          this.orderLoadingList[index].orderTemplate.deliveryDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/next item. Please set the valid timeframe to move.');
          }

        }
      } else {
        if (this.orderLoadingList[nextIndex].isOrder) {
          if (this.orderLoadingList[index].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[index].loadingTemplate.loadingDateTo
            && this.orderLoadingList[nextIndex].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[nextIndex].orderTemplate.deliveryDateTo) {
          let tempDateFrom = this.orderLoadingList[nextIndex].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[nextIndex].orderTemplate.deliveryDateFrom = this.orderLoadingList[index].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[index].loadingTemplate.loadingDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[nextIndex].orderTemplate.deliveryDateTo;
          this.orderLoadingList[nextIndex].orderTemplate.deliveryDateTo = this.orderLoadingList[index].loadingTemplate.loadingDateTo;
          this.orderLoadingList[index].loadingTemplate.loadingDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/next item. Please set the valid timeframe to move.');
          }

        } else {
          if (this.orderLoadingList[index].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[index].loadingTemplate.loadingDateTo
            && this.orderLoadingList[nextIndex].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[nextIndex].loadingTemplate.loadingDateTo) {
          let tempDateFrom = this.orderLoadingList[nextIndex].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[nextIndex].loadingTemplate.loadingDateFrom = this.orderLoadingList[index].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[index].loadingTemplate.loadingDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[nextIndex].loadingTemplate.loadingDateTo;
          this.orderLoadingList[nextIndex].loadingTemplate.loadingDateTo = this.orderLoadingList[index].loadingTemplate.loadingDateTo;
          this.orderLoadingList[index].loadingTemplate.loadingDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/next item. Please set the valid timeframe to move.');
          }
        }

      }
   }
  }
  moveItemUp(index) {
    let previousIndex = this.getPreviousActiveIndex(index);
    if (this.orderLoadingList[index] && previousIndex != null && this.orderLoadingList[previousIndex]) {
      if (this.orderLoadingList[index].isOrder) {
        if (this.orderLoadingList[previousIndex].isOrder) {
          if (this.orderLoadingList[index].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[index].orderTemplate.deliveryDateTo
            && this.orderLoadingList[previousIndex].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[previousIndex].orderTemplate.deliveryDateTo) {
          let tempDateFrom = this.orderLoadingList[previousIndex].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[previousIndex].orderTemplate.deliveryDateFrom = this.orderLoadingList[index].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[index].orderTemplate.deliveryDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[previousIndex].orderTemplate.deliveryDateTo;
          this.orderLoadingList[previousIndex].orderTemplate.deliveryDateTo = this.orderLoadingList[index].orderTemplate.deliveryDateTo;
          this.orderLoadingList[index].orderTemplate.deliveryDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/previous item. Please set the valid timeframe to move.');
          }
        } else {
          if (this.orderLoadingList[index].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[index].orderTemplate.deliveryDateTo
            && this.orderLoadingList[previousIndex].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[previousIndex].loadingTemplate.loadingDateTo) {
          let tempDateFrom = this.orderLoadingList[previousIndex].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[previousIndex].loadingTemplate.loadingDateFrom = this.orderLoadingList[index].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[index].orderTemplate.deliveryDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[previousIndex].loadingTemplate.loadingDateTo;
          this.orderLoadingList[previousIndex].loadingTemplate.loadingDateTo = this.orderLoadingList[index].orderTemplate.deliveryDateTo;
          this.orderLoadingList[index].orderTemplate.deliveryDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/previous item. Please set the valid timeframe to move.');
          }

        }
      } else {
        if (this.orderLoadingList[previousIndex].isOrder) {
          if (this.orderLoadingList[index].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[index].loadingTemplate.loadingDateTo
            && this.orderLoadingList[previousIndex].orderTemplate.deliveryDateFrom
            && this.orderLoadingList[previousIndex].orderTemplate.deliveryDateTo) {
          let tempDateFrom = this.orderLoadingList[previousIndex].orderTemplate.deliveryDateFrom;
          this.orderLoadingList[previousIndex].orderTemplate.deliveryDateFrom = this.orderLoadingList[index].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[index].loadingTemplate.loadingDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[previousIndex].orderTemplate.deliveryDateTo;
          this.orderLoadingList[previousIndex].orderTemplate.deliveryDateTo = this.orderLoadingList[index].loadingTemplate.loadingDateTo;
          this.orderLoadingList[index].loadingTemplate.loadingDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/previous item. Please set the valid timeframe to move.');
          }

        } else {
          if (this.orderLoadingList[index].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[index].loadingTemplate.loadingDateTo
            && this.orderLoadingList[previousIndex].loadingTemplate.loadingDateFrom
            && this.orderLoadingList[previousIndex].loadingTemplate.loadingDateTo) {
          let tempDateFrom = this.orderLoadingList[previousIndex].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[previousIndex].loadingTemplate.loadingDateFrom = this.orderLoadingList[index].loadingTemplate.loadingDateFrom;
          this.orderLoadingList[index].loadingTemplate.loadingDateFrom = tempDateFrom;
          let tempDateTo = this.orderLoadingList[previousIndex].loadingTemplate.loadingDateTo;
          this.orderLoadingList[previousIndex].loadingTemplate.loadingDateTo = this.orderLoadingList[index].loadingTemplate.loadingDateTo;
          this.orderLoadingList[index].loadingTemplate.loadingDateTo = tempDateTo;
          this.resetOrderLoadingList();
          this.form.form.markAsDirty();
          } else {
            this.notificationsService.error('Invalid time frame.',
             'Unable to move the order, Invalid time frame in selected/previous item. Please set the valid timeframe to move.');
          }
        }

      }
   }

  }
  getNextActiveIndex(index) {
    if ((index + 1) >= this.orderLoadingList.length)
      return null;
    for (let i = index + 1; i < this.orderLoadingList.length ; i++) {
      let item = this.orderLoadingList[i];
      if ((item.isOrder && !item.orderTemplate.deleted) || (!item.isOrder && !item.loadingTemplate.deleted)) {
        return i;
      }
    }
    return null;
  }

  getPreviousActiveIndex(index) {
    if ((index - 1) < 0)
      return null;
    for (let i = index - 1; i > -1 ; i--) {
      let item = this.orderLoadingList[i];
      if ((item.isOrder && !item.orderTemplate.deleted) || (!item.isOrder && !item.loadingTemplate.deleted)) {
        return i;
      }
    }
    return null;
  }
  resetOrderLoadingList() {
  this.orderLoadingList.splice(0, this.orderLoadingList.length);
  this.tourTemplateModel.orderTemplates.map(item => {
    return {
      isOrder: true,
      orderTemplate: item,
      loadingTemplate: null,
      dateTime: this.getSeconds(item.deliveryDateFrom)
      };
    } ).forEach(item => this.orderLoadingList.push(item));
  this.tourTemplateModel.loadingTemplates.map(item => {
      return {
        isOrder: false,
        orderTemplate: null,
        loadingTemplate: item,
        dateTime: this.getSeconds(item.loadingDateFrom)
        };
      } ).forEach(item => this.orderLoadingList.push(item));

  this.orderLoadingList.sort((a: any, b: any) => {
          if ( a['dateTime'] < b['dateTime'] ) {
            return -1;
          } else if ( a['dateTime'] > b['dateTime'] ) {
              return 1;
          } else {
            return 0;
          }
        });
 }
 getMinutes(dateFrom) {
   if (dateFrom == null) {
    return 0;
   }
   let d = new Date(dateFrom);
   return  ((d.getHours() * 60) + d.getMinutes());
 }
 getSeconds(dateFrom) {
  if (dateFrom == null) {
   return 0;
  }
  let d = new Date(dateFrom);
  return  ((d.getHours() * 60 * 60) + (d.getMinutes() * 60) + d.getSeconds());
}
 byTime(a, b) {
  return a.dateTime > b.dateTime ? 1 : -1;
 }
}



// WEBPACK FOOTER //
// ./src/app/recurrence/recurrent-tour-form.component.ts